import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  CREATE_PHASE,
  DELETE_PHASE,
  ErrorResponse,
  GET_CHANNEL_BY_ID,
  PATCH_PHASE,
  Phase,
} from '@frontend/data-access';
import { App, Button, Form, Input, Modal } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styles from './idea-channel-create-phase.module.less';

/* eslint-disable-next-line */
export interface IdeaChannelCreatePhaseProps {
  open: boolean;
  phase?: Phase;
  onFinish: () => void;
  onCancel: () => void;
  refetch: () => void;
}

interface FormValues {
  title: string;
  description: string;
}

export function IdeaChannelCreatePhase({
  open,
  phase,
  onCancel,
  onFinish,
  refetch,
}: IdeaChannelCreatePhaseProps) {
  const { notification } = App.useApp();
  const router = useRouter();
  const channelId = router.query['id'] as string;
  const [form] = Form.useForm();
  const { t } = useTranslation('common');
  const { t: t_admin } = useTranslation('admin');
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <ExclamationCircleOutlined />
        </span>
      ),
    });
  };

  const [deletePhase, { loading: loadingDelete }] = useMutation<boolean>(
    DELETE_PHASE,
    {
      onError: handleError,
      onCompleted: () => {
        refetch();
        setShowModal(false);
        onFinish();
      },
    }
  );

  const handleCompleted = () => {
    form.resetFields();
    onFinish();
    notification.success({
      message: phase ? t('phase_updated') : t('phase_added'),
      icon: (
        <span className="text-green-600">
          <CheckCircleOutlined />
        </span>
      ),
    });
  };

  const [create, { loading: createLoading }] = useMutation(CREATE_PHASE, {
    onCompleted: handleCompleted,
    refetchQueries: [
      {
        query: GET_CHANNEL_BY_ID,
        variables: {
          id: channelId,
        },
      },
    ],
  });
  const [update, { loading: patchLoading }] = useMutation(PATCH_PHASE, {
    onCompleted: handleCompleted,
  });

  const handleOnFinish = (values: FormValues) => {
    if (phase?.id) {
      update({
        variables: {
          input: {
            id: phase?.id,
            title: values.title,
            description: values.description,
          },
        },
      });
    } else {
      create({
        variables: {
          input: {
            title: values.title,
            description: values.description,
          },
        },
      });
    }
  };

  const isNotEditable = phase?.title === 'Pending decision';

  useEffect(() => {
    if (phase) {
      form.setFieldsValue({
        title: phase.title,
        description: phase.description,
      });
    } else {
      form.resetFields();
    }
  }, [phase, form]);

  return (
    <div className={styles['container']}>
      <Modal
        title={phase ? t('edit_phase') : t('add_phase')}
        open={open}
        onCancel={onCancel}
        footer={
          <div>
            <div className="flex justify-start">
              <Button
                type="primary"
                shape="round"
                loading={createLoading || patchLoading}
                disabled={createLoading || patchLoading}
                onClick={() => form.submit()}
                data-test="save-btn"
              >
                {phase ? t('save') : t('create')}
              </Button>
            </div>
            {phase && (
              <div className="flex justify-start items-center pt-6 border-t border-solid border-gray-400 mt-6">
                <Button
                  loading={loadingDelete}
                  className="flex-shrink-0 text-red-600"
                  data-test="delete-phase-btn"
                  icon={<DeleteOutlined />}
                  shape="round"
                  variant="outlined"
                  color="danger"
                  onClick={() => setShowModal(true)}
                >
                  {t('delete')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className="pt-4">
          <Form form={form} layout="vertical" onFinish={handleOnFinish}>
            <Form.Item
              name="title"
              label={t('phase_name')}
              rules={[{ required: true, message: t('enter_name') }]}
            >
              <Input
                maxLength={50}
                data-test="phase-name"
                placeholder={t('enter_phase_name')}
                disabled={isNotEditable}
              />
            </Form.Item>
            <Form.Item
              name="description"
              label={t('description')}
              rules={[
                { required: true, message: t('enter_description_required') },
              ]}
            >
              <Input.TextArea
                data-test="phase-desc"
                placeholder={t('enter_description')}
                rows={3}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        <div className="gap-4 flex flex-col items-center">
          <WarningOutlined className="!text-orange-500 text-6xl" />
          <p className="font-semibold">{t('confirm_remove')}</p>
          <p>{t_admin('idea_hub_delete_phase_confirm')}</p>
          <div className="space-x-4">
            <Button
              className="flex-shrink-0 "
              data-test="delete-phase-btn"
              shape="round"
              onClick={() => setShowModal(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              loading={loadingDelete}
              className="flex-shrink-0"
              data-test="delete-phase-btn"
              icon={<DeleteOutlined />}
              shape="round"
              color="danger"
              variant="solid"
              onClick={() =>
                phase
                  ? deletePhase({
                      variables: {
                        id: phase.id,
                      },
                    })
                  : undefined
              }
            >
              {t('delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default IdeaChannelCreatePhase;
